/***
 *
 *   DIAGRAM
 *   One trigger's workflow: Starts → Intake · Verify · Screen · Assess ·
 *   Monitor → Written back, in a 7-column grid with a chevron in each gap.
 *   Each stage column sets data-wf so its icon and steps take the stage
 *   colour; the Starts column stays platform blue and Written back is green.
 *
 *   PROPS
 *   trigger: a view-model trigger
 *   expanded: Expand all (details inline, slide-sized frame)
 *   onOpen({ trigger, stage, step, isStart }, element): open the step drawer
 *
 **********/

import React from "react";
import { PublicLinkBox, SettingsLink, cx } from "./details";
import { STAGES, StageIcon } from "./stage-icon";
import { StartCard, StepCard } from "./step-card";
import styles from "./workflow.module.scss";

/* the eyebrow already leads with the source ("XPM · Full AML check"); add
   it only when it doesn't, so it never reads "XPM · XPM · …" */
function eyebrowText(trigger) {
  const eyebrow = trigger.eyebrow || "";
  const source = trigger.source || "";
  if (!source) return eyebrow;
  if (!eyebrow) return source;
  return eyebrow.startsWith(source) ? eyebrow : `${source} · ${eyebrow}`;
}

function ColumnHead({ icon, name, line }) {
  return (
    <div className={styles.chead}>
      <StageIcon name={icon} />
      <div>
        <span className={styles.cheadName}>{name}</span>
        <small>{line}</small>
      </div>
    </div>
  );
}

export function Diagram({ trigger, expanded, onOpen }) {
  const start = trigger.start || {};
  const stages = trigger.stages || {};
  const writtenBack = Array.isArray(trigger.written_back) ? trigger.written_back : [];
  const headingId = `wf-h-${trigger.id}`;

  return (
    <section className={cx(styles.frame, expanded && styles.expanded)} aria-labelledby={headingId}>
      <div className={styles.fhead}>
        <div>
          <div className={styles.eb}>{eyebrowText(trigger)}</div>
          <h2 id={headingId}>{trigger.title}</h2>
          {trigger.summary && <p>{trigger.summary}</p>}
        </div>
        <div className={styles.legend} aria-hidden="true">
          <span>
            <i className={styles.lgStart} />
            Starts here
          </span>
          <span>
            <i className={styles.lgOff} />
            Not switched on
          </span>
          <span>
            <i className={styles.lgOk} />
            Written back
          </span>
        </div>
      </div>

      <div className={styles.flow}>
        <div className={styles.col} data-col="start">
          <ColumnHead icon="start" name="Starts" line="The trigger" />
          {start.title && (
            <StartCard
              start={start}
              expanded={expanded}
              onOpen={(el) => onOpen({ trigger, stage: null, step: start, isStart: true }, el)}
            />
          )}
          <PublicLinkBox link={start.public_link} />
          {start.settings?.path && (
            <div>
              <SettingsLink settings={start.settings} />
            </div>
          )}
        </div>

        {STAGES.map((stage) => {
          const steps = Array.isArray(stages[stage.slug]) ? stages[stage.slug] : [];
          return (
            <div key={stage.slug} className={styles.col} data-col={stage.slug} data-wf={stage.slug}>
              <ColumnHead icon={stage.slug} name={stage.name} line={stage.line} />
              {steps.length ? (
                steps.map((step, i) => (
                  <StepCard
                    key={step.id || i}
                    step={step}
                    expanded={expanded}
                    onOpen={(el) => onOpen({ trigger, stage: stage.slug, step }, el)}
                  />
                ))
              ) : (
                <div className={styles.empty}>Not part of this workflow</div>
              )}
            </div>
          );
        })}

        <div className={styles.col} data-col="written">
          <ColumnHead icon="written" name="Written back" line="Where the result lands" />
          {writtenBack.length > 0 && (
            <div className={styles.out}>
              {writtenBack.map((group, i) => (
                <div key={i} className={styles.outg}>
                  <span className={styles.outDest}>{group.destination}</span>
                  {group.when && <span className={styles.outWhen}> · {group.when}</span>}
                  <ul>
                    {(group.items || []).map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
